/**
 * Entering the figures a valuation needs, by hand, from a spreadsheet, or as a P&L.
 *
 * Six numbers a year and no more: EBITDA, capex, working capital, tax paid, cash and
 * debt. That is the entire contract `app/valuation/v2/valuation.py` reads, which is
 * why a form is a realistic way to start a valuation at all - nobody would fill in a
 * balance sheet to get a discounted cash flow.
 *
 * The three modes differ only in what they ask for:
 *
 *   form   the six, directly
 *   pnl    revenue, operating profit and depreciation - the shape a filing prints -
 *          from which EBITDA follows, with working capital as days of revenue
 *   excel  a pasted block from a spreadsheet, because retyping a column somebody
 *          already has is the fastest way to introduce a transcription error
 *
 * All three post to the same endpoint and are stored identically, so a valuation
 * begun one way can be continued another.
 */

import { useState } from 'react';

import {
  useValuationStore,
  type SourceKind,
  type Units,
  type YearFigures,
} from '../../../store/valuation-projects';

/** The six, in the order they appear in the free cash flow. */
const FIELDS: { key: string; label: string; hint: string }[] = [
  { key: 'ebitda', label: 'EBITDA', hint: 'Earnings before interest, tax and depreciation' },
  { key: 'capex', label: 'Capex', hint: 'Spent on fixed assets. Positive.' },
  {
    key: 'workingCapital',
    label: 'Working capital',
    hint: 'Stocks and receivables less payables, at the year end. A level, not a movement.',
  },
  { key: 'taxPaid', label: 'Tax paid', hint: 'Cash tax in the year. Positive.' },
  { key: 'cash', label: 'Cash', hint: 'At the year end.' },
  { key: 'debt', label: 'Debt', hint: 'Borrowings at the year end. Positive.' },
];

/**
 * What a cut-down profit and loss asks for instead.
 *
 * EBITDA is not asked for directly: someone reading a filing has operating profit
 * and the depreciation and amortisation beneath it, and adding those up is
 * arithmetic the software should do rather than the user.
 *
 * Working capital is asked for as DAYS of revenue, because a P&L has no working
 * capital balance in it - that is a balance sheet figure. Days is how anyone without
 * one thinks about it, and the backend turns it into a level.
 */
const PNL_FIELDS: { key: string; label: string; hint: string }[] = [
  { key: 'revenue', label: 'Revenue', hint: 'Turnover for the year' },
  {
    key: 'operatingProfit',
    label: 'Operating profit',
    hint: 'Before interest and tax. EBITDA is this plus the charges below.',
  },
  { key: 'depreciation', label: 'Depreciation', hint: 'Positive' },
  { key: 'amortisation', label: 'Amortisation', hint: 'Positive' },
  { key: 'capex', label: 'Capex', hint: 'Spent on fixed assets. Positive.' },
  { key: 'taxPaid', label: 'Tax paid', hint: 'Cash tax in the year. Positive.' },
  {
    key: 'workingCapitalDays',
    label: 'Working capital days',
    hint: 'Days of revenue tied up. 30 means a month of revenue.',
  },
  { key: 'cash', label: 'Cash', hint: 'At the year end.' },
  { key: 'debt', label: 'Debt', hint: 'Borrowings at the year end. Positive.' },
];

const BLANK_PNL: Record<string, number> = Object.fromEntries(
  PNL_FIELDS.map((field) => [field.key, 0]),
);

const BLANK: YearFigures = {
  ebitda: 0,
  capex: 0,
  workingCapital: 0,
  taxPaid: 0,
  cash: 0,
  debt: 0,
};

function thisYear(): number {
  return new Date().getFullYear();
}

export default function ValuationFiguresForm({
  kind,
  name,
  units,
  onCreated,
}: {
  kind: Exclude<SourceKind, 'linked'>;
  name: string;
  units: Units;
  onCreated: (id: string) => void;
}) {
  const create = useValuationStore((s) => s.create);
  const parseWorkbook = useValuationStore((s) => s.parseWorkbook);
  const isSaving = useValuationStore((s) => s.isSaving);

  // Three years by default. One cannot give a change in working capital, and the
  // gap report says so - but starting at one would make that the normal case.
  const [years, setYears] = useState<number[]>(() => {
    const first = thisYear();
    return [first, first + 1, first + 2];
  });
  const [rows, setRows] = useState<Record<number, YearFigures>>(() => {
    const first = thisYear();
    return { [first]: { ...BLANK }, [first + 1]: { ...BLANK }, [first + 2]: { ...BLANK } };
  });
  const [pasted, setPasted] = useState('');
  // What the parser said it did. Shown before anything is created, because a sheet
  // that yields three of five years produces figures that look complete.
  const [report, setReport] = useState<string[] | null>(null);
  // The method blocks a spreadsheet held, carried to creation rather than applied to
  // the six-column grid: they are not years and there is nowhere on that grid to put
  // them. The forms in the workspace pick them up from the saved config.
  const [methodInputs, setMethodInputs] = useState<Record<string, unknown> | null>(
    null,
  );
  // The P&L keeps its own rows: its columns are not the six, and holding both in one
  // shape would mean a grid where half the cells mean nothing.
  const [pnlRows, setPnlRows] = useState<Record<number, Record<string, number>>>(
    () => {
      const first = thisYear();
      return {
        [first]: { ...BLANK_PNL },
        [first + 1]: { ...BLANK_PNL },
        [first + 2]: { ...BLANK_PNL },
      };
    },
  );

  const setCell = (year: number, field: keyof YearFigures, raw: string) => {
    const value = raw.trim() === '' ? 0 : Number(raw.replace(/,/g, ''));
    if (Number.isNaN(value)) return;
    setRows((current) => ({
      ...current,
      [year]: { ...(current[year] ?? BLANK), [field]: value },
    }));
  };

  const setPnlCell = (year: number, field: string, raw: string) => {
    const value = raw.trim() === '' ? 0 : Number(raw.replace(/,/g, ''));
    if (Number.isNaN(value)) return;
    setPnlRows((current) => ({
      ...current,
      [year]: { ...(current[year] ?? BLANK_PNL), [field]: value },
    }));
  };

  const addYear = () => {
    const next = (years[years.length - 1] ?? thisYear()) + 1;
    setYears([...years, next]);
    setRows((current) => ({ ...current, [next]: { ...BLANK } }));
    setPnlRows((current) => ({ ...current, [next]: { ...BLANK_PNL } }));
  };

  /**
   * A block pasted from a spreadsheet: one row per year, tab or comma separated,
   * in the column order of FIELDS with the year first.
   *
   * Parsed rather than uploaded because the clipboard is where the figures already
   * are. A file upload would mean saving a sheet, choosing it, and mapping columns -
   * three steps to avoid one paste.
   */
  const applyPaste = () => {
    const parsedYears: number[] = [];
    const parsedRows: Record<number, YearFigures> = {};
    for (const line of pasted.split('\n')) {
      const cells = line.split(/[\t,;]/).map((cell) => cell.trim());
      if (cells.length < 2) continue;
      const year = Number(cells[0]);
      if (!Number.isInteger(year) || year < 1900 || year > 2200) continue;
      const entry = { ...BLANK };
      FIELDS.forEach((field, index) => {
        const raw = cells[index + 1];
        if (raw === undefined || raw === '') return;
        const value = Number(raw.replace(/[,()£$]/g, ''));
        if (!Number.isNaN(value)) {
          // Parenthesised figures are negative in accounts.
          // Indexed through the key, which is a plain string now that the same
          // column list serves both the six figures and the P&L's own fields.
          (entry as Record<string, number>)[field.key] =
            /^\(.*\)$/.test(raw) ? -value : value;
        }
      });
      parsedYears.push(year);
      parsedRows[year] = entry;
    }
    if (parsedYears.length === 0) return;
    setYears(parsedYears.sort((a, b) => a - b));
    setRows(parsedRows);
  };

  const isPnl = kind === 'pnl';
  const columns = isPnl ? PNL_FIELDS : FIELDS;

  const submit = async () => {
    // A P&L goes as `pnl`, so the backend derives EBITDA from operating profit and
    // the non-cash charges, and working capital from days of revenue. Sending it as
    // `figures` - which this did - silently discarded both conversions.
    // Sent only where the file held some, so a valuation created without one keeps
    // the backend's own defaults rather than a config assembled here.
    const config = methodInputs ? { methodInputs } : undefined;
    const id = await create(
      kind === 'pnl'
        ? {
            name,
            source: { kind, units },
            config,
            pnl: Object.fromEntries(
              years.map((year) => [String(year), pnlRows[year] ?? BLANK_PNL]),
            ),
          }
        : {
            name,
            source: { kind, units },
            config,
            figures: {
              years: Object.fromEntries(
                years.map((year) => [String(year), rows[year] ?? BLANK]),
              ),
            },
          },
    );
    if (id) onCreated(id);
  };

  return (
    <div className="mt-4">
      {kind === 'excel' && (
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-700">
            Upload a spreadsheet
            <input
              type="file"
              accept=".xlsx"
              className="mt-1 block w-full text-xs"
              onChange={async (event) => {
                const file = event.target.files?.[0];
                // Cleared so the same file can be retried after a failure;
                // without this the change event never fires again.
                event.target.value = '';
                if (!file) return;
                const read = await parseWorkbook(file);
                if (!read) return;
                // Both reports, together: the second reader's notes say which
                // labels it recognised and every rate it read as a percentage, and
                // that is the reading most likely to be wrong by a hundred.
                setReport([...read.notes, ...read.methodNotes]);
                setMethodInputs(read.methodInputs);
                const found = Object.keys(read.years)
                  .map(Number)
                  .filter((year) => Number.isFinite(year))
                  .sort((a, b) => a - b);
                if (found.length > 0) {
                  setYears(found);
                  setRows(
                    Object.fromEntries(
                      found.map((year) => [
                        year,
                        { ...BLANK, ...read.years[String(year)] },
                      ]),
                    ),
                  );
                }
              }}
            />
            <span className="mt-1 block font-normal text-gray-500">
              An .xlsx with a row of headings — a Year column and any of EBITDA,
              capex, working capital, tax, cash or debt — and a row per year. The
              sheet and the headings are found; they do not have to be first.
            </span>
          </label>

          {/* What it actually read, before anything is created from it. */}
          {report && (
            <ul className="mt-2 space-y-1 rounded border border-blue-200 bg-blue-50 p-2 text-xs text-blue-900">
              {report.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          )}

          <div className="mt-3 text-xs text-gray-400">or</div>

          <label className="mt-1 block text-xs font-medium text-gray-700">
            Paste from a spreadsheet
            <textarea
              value={pasted}
              onChange={(event) => setPasted(event.target.value)}
              rows={4}
              placeholder={`2024\t1000\t200\t50\t100\t500\t0\n2025\t1200\t200\t60\t120\t900\t0`}
              className="mt-1 block w-full rounded border border-gray-300 px-2 py-1.5 font-mono text-xs"
            />
            <span className="mt-1 block font-normal text-gray-500">
              One row per year: year, then {FIELDS.map((f) => f.label).join(', ')}.
              Tabs, commas or semicolons. Figures in brackets are read as negative.
            </span>
          </label>
          <button
            type="button"
            onClick={applyPaste}
            className="mt-2 rounded border border-gray-300 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
          >
            Read it
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr>
              <th className="border-b border-gray-200 px-2 py-1.5 text-left font-medium text-gray-500">
                Year
              </th>
              {columns.map((field) => (
                <th
                  key={field.key}
                  title={field.hint}
                  className="border-b border-gray-200 px-2 py-1.5 text-right font-medium text-gray-500"
                >
                  {field.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {years.map((year) => (
              <tr key={year}>
                <td className="border-b border-gray-100 px-2 py-1 font-medium text-gray-700">
                  {year}
                </td>
                {columns.map((field) => (
                  <td key={field.key} className="border-b border-gray-100 px-1 py-1">
                    <input
                      inputMode="decimal"
                      value={
                        isPnl
                          ? (pnlRows[year]?.[field.key] ?? 0)
                          : (rows[year]?.[field.key as keyof YearFigures] ?? 0)
                      }
                      onChange={(event) =>
                        isPnl
                          ? setPnlCell(year, field.key, event.target.value)
                          : setCell(
                              year,
                              field.key as keyof YearFigures,
                              event.target.value,
                            )
                      }
                      className="w-24 rounded border border-gray-200 px-1.5 py-1 text-right tabular-nums"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isPnl && (
        <p className="mt-2 text-xs text-gray-500">
          EBITDA is worked out as operating profit plus depreciation and
          amortisation. Working capital comes from the days above, applied to
          revenue.
        </p>
      )}

      <div className="mt-3 flex items-center gap-3">
        <button
          type="button"
          onClick={addYear}
          className="rounded border border-gray-300 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
        >
          Add a year
        </button>
        <button
          type="button"
          disabled={isSaving}
          onClick={() => void submit()}
          className="rounded bg-gray-900 px-3 py-1.5 text-sm text-white disabled:opacity-40"
        >
          {isSaving ? 'Valuing…' : 'Create it'}
        </button>
        <span className="text-xs text-gray-500">
          Anything missing is reported rather than guessed at.
        </span>
      </div>
    </div>
  );
}
